import { v4 as uuidv4 } from 'uuid';
import * as actionType from './actionTypes';

// Ensures a rule has a stable instanceId and a `catalog` snapshot — the original
// API value captured at load. The stored value is later reduced to a display-only
// shape as the user edits; `catalog` preserves API-only fields (e.g. `code`) so
// `mapToApiFromStore` can restore them on save.
const prepareRule = r => ({
  ...r,
  instanceId: r.instanceId || uuidv4(),
  catalog: r.catalog || r.value,
});

const initialState = {
  products: [],
  productsLoading: true,
  productsError: null,
  selectedProduct: '',
  rules: [],
  isRulesLoading: false,
  rulesError: null,
  hasUnsavedChanges: false,
  fieldOverrides: {},
  pendingActivations: {},
  nextEffectiveFrom: null,
};

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case actionType.SET_PRODUCTS:
      // When products are set, also stop the loading indicator and
      // default the selected product to the first item's id (or empty string).
      return {
        ...state,
        products: action.payload,
        productsLoading: false,
      };
    case actionType.SET_PRODUCTS_LOADING:
      // When loading starts, also clear any previous products error.
      // When loading is stopped (false), preserve the existing error.
      return {
        ...state,
        productsLoading: action.payload,
        productsError: action.payload ? null : state.productsError,
      };
    case actionType.SET_PRODUCTS_ERROR:
      // When there's a products error, also ensure loading is stopped.
      return {
        ...state,
        productsError: action.payload,
        productsLoading: false,
      };
    case actionType.SET_SELECTED_PRODUCT:
      return { ...state, selectedProduct: action.payload };
    case actionType.SET_RULES: {
      if (action.payload.productCode !== state.selectedProduct) return state;
      return {
        ...state,
        rules: action.payload.rules.map(prepareRule),
        fieldOverrides: {},
        hasUnsavedChanges: false,
      };
    }
    case actionType.SET_RULES_LOADING:
      return { ...state, isRulesLoading: action.payload };
    case actionType.SET_RULES_ERROR:
      return { ...state, rulesError: action.payload };
    case actionType.SET_NEXT_EFFECTIVE_FROM:
      return { ...state, nextEffectiveFrom: action.payload };
    case actionType.SET_HAS_UNSAVED_CHANGES:
      return { ...state, hasUnsavedChanges: action.payload };
    case actionType.SET_PENDING_ACTIVATION: {
      const { productCode, activationDate } = action.payload;
      const pendingActivations = { ...state.pendingActivations };
      if (activationDate) {
        pendingActivations[productCode] = activationDate;
      } else {
        delete pendingActivations[productCode];
      }
      return { ...state, pendingActivations };
    }
    case actionType.UPDATE_RULE: {
      // update rule
      // if no payload or no ruleId provided, do nothing
      if (!action.payload) return state;
      const { ruleId, changes } = action.payload;
      if (typeof ruleId === 'undefined') return state;
      let updated = false;
      const newRules = state.rules.map(rule => {
        if (rule && rule.instanceId === ruleId) {
          updated = true;
          return { ...rule, ...changes };
        }
        return rule;
      });
      return {
        ...state,
        rules: newRules,
        // only mark unsaved when an actual rule was modified
        hasUnsavedChanges: updated ? true : state.hasUnsavedChanges,
      };
    }
    case actionType.ADD_RULE: {
      const newRule = prepareRule(action.payload);
      return {
        ...state,
        rules: [...state.rules, newRule],
        hasUnsavedChanges: true,
      };
    }
    case actionType.ADD_RULE_SORTED: {
      return {
        ...state,
        rules: action.payload.map(prepareRule),
        hasUnsavedChanges: true,
      };
    }
    case actionType.REMOVE_RULE: {
      const instanceId = action.payload;
      const idx = state.rules.findIndex(r => r.instanceId === instanceId);
      if (idx === -1) return state;
      const rules = [
        ...state.rules.slice(0, idx),
        ...state.rules.slice(idx + 1),
      ];
      return { ...state, rules, hasUnsavedChanges: true };
    }
    default:
      return state;
  }
};

export default reducer;
